import React from 'react';
import { Plus, FolderPlus, Trash2 } from 'lucide-react';
import ConditionRow from './ConditionRow';
import { emptyCondition, emptyGroup } from './criteriaUtils';

const ToggleOp = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-hairline bg-[#F8FAFC] p-0.5 text-xs font-medium" role="radiogroup" aria-label="Combine conditions with">
    {[
      { v: 'AND', label: 'All of' },
      { v: 'OR', label: 'Any of' },
    ].map((o) => (
      <button
        key={o.v}
        type="button"
        role="radio"
        aria-checked={value === o.v}
        onClick={() => onChange(o.v)}
        className={`rounded-md px-2.5 py-1 transition-colors ${
          value === o.v ? 'bg-white text-[#18181B] shadow-sm' : 'text-zinc-500 hover:text-[#18181B]'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * A criteria group: "All of" / "Any of" header, children (conditions or nested groups),
 * and add/remove controls. The root group is rendered without a card border.
 */
const CriteriaGroupCard = ({ node, path, onChange, onRemove, onAppend, onDuplicate, onWrap, onMoveOut, newPaths }) => {
  const isRoot = path.length === 0;
  const children = node.children || [];
  const setOp = (op) => onChange(path, (n) => ({ ...n, op }));
  const joiner = node.op === 'OR' ? 'or' : 'and';

  const body = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-zinc-600">{isRoot ? 'Include profiles matching' : 'Matching'}</span>
        {node.display_name ? (
          <span className="text-sm font-medium text-[#18181B]" title={node.offer_feature ? `${node.offer_feature} = ${node.offer_value_label || node.offer_value || ''}` : undefined}>
            {node.display_name}
          </span>
        ) : null}
        <ToggleOp value={node.op || 'AND'} onChange={setOp} />
        <span className="text-sm text-zinc-600">the following:</span>
        {!isRoot ? (
          <button
            type="button"
            onClick={() => onRemove(path)}
            className="ml-auto inline-flex items-center gap-1 rounded p-1.5 text-xs text-zinc-400 hover:bg-pink-50 hover:text-[#F53D7A]"
            title="Remove group"
            aria-label="Remove group"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="mt-3 space-y-2">
        {children.length === 0 ? (
          <p className="rounded-lg border border-dashed border-hairline px-4 py-6 text-center text-sm text-zinc-400">
            No conditions yet. Add one below.
          </p>
        ) : null}
        {children.map((child, i) => {
          const childPath = [...path, i];
          const key = childPath.join('-');
          const el =
            child.kind === 'group' ? (
              <CriteriaGroupCard
                key={key}
                node={child}
                path={childPath}
                onChange={onChange}
                onRemove={onRemove}
                onAppend={onAppend}
                onDuplicate={onDuplicate}
                onWrap={onWrap}
                onMoveOut={onMoveOut}
                newPaths={newPaths}
              />
            ) : (
              <ConditionRow
                key={key}
                condition={child}
                path={childPath}
                onChange={onChange}
                onRemove={onRemove}
                onDuplicate={onDuplicate}
                onWrap={onWrap}
                onMoveOut={onMoveOut}
                autoOpenPicker={newPaths?.has(key)}
              />
            );
          return (
            <React.Fragment key={key}>
              {i > 0 ? (
                <div className="flex items-center gap-2 pl-2" aria-hidden>
                  <span className="rounded bg-[#E8EEF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{joiner}</span>
                  <span className="h-px flex-1 bg-hairline" />
                </div>
              ) : null}
              {el}
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onAppend(path, emptyCondition())}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#0062FB]/30 bg-blue-50/40 px-3 py-1.5 text-sm font-medium text-[#0062FB] hover:bg-blue-50"
        >
          <Plus className="h-4 w-4" />
          Add condition
        </button>
        <button
          type="button"
          onClick={() => onAppend(path, emptyGroup())}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-zinc-600 hover:bg-[#E8EEF5] hover:text-[#18181B]"
          title="Add a nested group to mix and / or"
        >
          <FolderPlus className="h-4 w-4" />
          Add group
        </button>
      </div>
    </>
  );

  if (isRoot) return <div>{body}</div>;
  return <div className="rounded-xl border border-hairline bg-[#F8FAFC] p-3 pl-4 shadow-[inset_3px_0_0_#e2e8f0]">{body}</div>;
};

export default CriteriaGroupCard;
